import { useReducer, useRef } from 'react'
import useControlled from './useControlled'
import { useCompareDeepEffect } from 'hooks/useCompareDeepEffect.js'

// implements a table property that can be controlled or uncontrolled
// returns the controlled value and a reducer function for setting it
const useControlledTableProperty = ({ initialValue, controlledValue, name, onChangeFn, overrideProp = undefined }) => {
  // construct a controlled property
  const [prop, setProp] = useControlled({
    controlledValue,
    initialValue,
    name: 'XtractTable.' + name
  })

  // define a local state variable that the table maintains
  // it is initialized to the property
  const [tableState, setTableState] = useReducer((oldState, newStateFn) => {
    const newState = typeof newStateFn === 'function' ? newStateFn(oldState) : newStateFn
    if(!_.isEqual(prop, newState)) {
      // new state from a reducer is a new state object even if it is equivalent to the old state object.
      // this means that if the prop is an object or array, doing setProp will cause a rerender/change even
      // if they are equivalent because the refs will be different. The deep comparison above (isEqual)
      // will prevent this by only calling setProp if the array or object actually contains different data
      setProp(newState)
    }
    return newState
  }, prop)

  // When the controlled prop changes externally (e.g. the parent sets a new
  // selection programmatically), keep the internal reducer state in sync.
  // Without this, tableState can drift from prop, causing the reducer to
  // produce a newState that deep equals a stale previous tableState, which
  // then suppresses the useCompareDeepEffect below and silently drops the
  // onChange notification back to the parent.
  const syncingRef = useRef(false)
  useCompareDeepEffect(() => {
    if (!_.isEqual(prop, tableState)) {
      syncingRef.current = true
      setTableState(prop)
    }
  }, [prop])

  // whenever the table state is changed, call the parent's onChange function (if it exists)
  useCompareDeepEffect(() => {
    if (syncingRef.current) {
      // this tableState update came from an external prop sync, not a user
      // action, do not echo it back to the parent
      syncingRef.current = false
      return
    }
    onChangeFn?.(tableState)
  }, [tableState])

  // return the property and its setter
  return [prop, setTableState]
}

export default useControlledTableProperty